import { Tooltip } from "antd";
import clsx from "clsx";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "./TableContent.scss";

export type TableContentProps = {
  children?: ReactNode;
  name?: string;
  className?: string;
};

const getTextContent = (children: ReactNode): string | undefined => {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  return undefined;
};

export const TableContent = memo(
  ({ children, name, className }: TableContentProps) => {
    const contentRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = useCallback(() => {
      const element = contentRef.current;

      if (!element) {
        return;
      }

      setIsTruncated(element.scrollWidth > element.clientWidth);
    }, []);

    useEffect(() => {
      checkTruncation();

      const element = contentRef.current;

      if (!element || typeof ResizeObserver === "undefined") {
        return;
      }

      const observer = new ResizeObserver(checkTruncation);
      observer.observe(element);

      return () => observer.disconnect();
    }, [checkTruncation, children, name]);

    const tooltipTitle = name ?? getTextContent(children);
    const showTooltip = Boolean(tooltipTitle) && (Boolean(name) || isTruncated);

    const content = (
      <span ref={contentRef} className={clsx("tableContent", className)}>
        {children}
      </span>
    );

    if (!showTooltip || !tooltipTitle) {
      return content;
    }

    return (
      <Tooltip title={tooltipTitle} mouseEnterDelay={0.35}>
        {content}
      </Tooltip>
    );
  },
);

TableContent.displayName = "TableContent";
